import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export const useGmailSyncHealth = () => {
  const [latestByColab, setLatestByColab] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Último run por colaborador (DISTINCT ON equivalent via ordenação)
      const { data: allRuns, error: e1 } = await supabase
        .from('gmail_sync_runs')
        .select('colaborador_id, status, started_at, ended_at, duration_ms, emails_fetched, emails_inserted, duplicates_skipped, error_message, timeout_hit, run_type')
        .order('started_at', { ascending: false })
        .limit(1000);
      if (e1) throw e1;

      const seen = new Set();
      const latest = [];
      for (const r of (allRuns || [])) {
        if (!seen.has(r.colaborador_id)) {
          seen.add(r.colaborador_id);
          latest.push(r);
        }
      }
      setLatestByColab(latest);

      // Últimos 50 runs
      const { data: recent, error: e2 } = await supabase
        .from('gmail_sync_runs')
        .select('id, colaborador_id, run_type, status, started_at, duration_ms, emails_fetched, emails_inserted, duplicates_skipped, timeout_hit, error_message')
        .order('started_at', { ascending: false })
        .limit(50);
      if (e2) throw e2;
      setRecentRuns(recent || []);

      // Métricas 7 dias (agregação em JS)
      const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: weekly, error: e3 } = await supabase
        .from('gmail_sync_runs')
        .select('colaborador_id, status, emails_inserted, duration_ms')
        .gte('started_at', cutoff);
      if (e3) throw e3;

      const byColab = new Map();
      for (const r of (weekly || [])) {
        const k = r.colaborador_id;
        const agg = byColab.get(k) || { colaborador_id: k, total_runs: 0, completed: 0, errors: 0, timeouts: 0, total_inserted: 0, _durations: [] };
        agg.total_runs += 1;
        if (r.status === 'completed') agg.completed += 1;
        else if (r.status === 'error') agg.errors += 1;
        else if (r.status === 'timeout') agg.timeouts += 1;
        agg.total_inserted += (r.emails_inserted || 0);
        if (r.duration_ms) agg._durations.push(r.duration_ms);
        byColab.set(k, agg);
      }
      const stats = [...byColab.values()].map((a) => ({
        ...a,
        avg_duration_ms: a._durations.length ? Math.round(a._durations.reduce((s, v) => s + v, 0) / a._durations.length) : null,
      })).sort((a, b) => a.colaborador_id.localeCompare(b.colaborador_id));
      setWeeklyStats(stats);
    } catch (err) {
      console.error('useGmailSyncHealth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { latestByColab, recentRuns, weeklyStats, loading, error, refetch: fetchAll };
};
